
import { Component, Vue, Prop } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import { cloneDeep } from 'lodash';
import { Validate } from '@/types';
import Toast from '@/utils/toast';
import StandardModal from '@/components/shared/suites/StandardModal.vue';
import TextButton from '@/components/shared/suites/TextButton.vue';
import TextField from '@/components/shared/TextField.vue';
import SelectComponent from '@/components/shared/Select.vue';
import ExpansionPanel from '@/components/shared/suites/panels/ExpansionPanel.vue';
import ToggleSwitch from '@/components/shared/ToggleSwitch.vue';
import CustomQuestionsList from '@/components/advanced/custom-questions/CustomQuestionsList.vue';

const namespace: string = 'inquiryForm';

@Component<InquiryFormModal>({
	components: {
		StandardModal,
		TextButton,
		TextField,
		SelectComponent,
		ExpansionPanel,
		ToggleSwitch,
		CustomQuestionsList,
		ValidationObserver,
		ValidationProvider
	}
})
export default class InquiryFormModal extends Vue {
	@Action('fetchInquiryForm', { namespace }) private fetchInquiryForm!: () => Promise<void>;
	@Action('updateInquiryForm', { namespace }) private updatedInquiryForm!: (form: InquiryForm) => Promise<void>;
	@Getter('getInquiryForm', { namespace }) private inquiryForm!: InquiryForm;
	@Getter('getRestaurant', { namespace: 'auth' }) private restaurant!: Restaurant;
	@Getter('getInquiryFormQuestions', { namespace: 'customQuestions' }) private inquiryQuestions!: CustomQuestion[];
	@Prop({ type: Boolean, required: true }) private showModal!: boolean;

	$refs!: {
		observer: Validate;
		formDetailsPanel: ExpansionPanel;
		customFieldsPanel: ExpansionPanel;
	};

	private isEditing: boolean = false;
	private loading: boolean = false;
	private saving: boolean = false;
	private selectedLocale: RestaurantLocale = {} as RestaurantLocale;
	private mutableInquiryForm: InquiryForm | null = null;

	private defaultFields: string[] = [
		'first_name',
		'last_name',
		'email',
		'phone',
		'event_date',
		'guest_count'
	];

	private get isDefaultLocale(): boolean {
		return !this.selectedLocale || !Object.keys(this.selectedLocale).length || (this.selectedLocale && this.selectedLocale.is_default);
	}

	private get inquiryFormUrl(): string {
		if (!this.inquiryForm) {
			return '';
		}
		return `${process.env.VUE_APP_MENU_DOMAIN}/${this.restaurant.slug}/inquiry-form?catering=true`;
	}

	private async created(): Promise<void> {
		this.setLocaleToDefault();
		this.loading = true;
		try {
			await this.fetchInquiryForm();
			this.mutableInquiryForm = cloneDeep(this.inquiryForm);
			this.loading = false;
		}
		catch {
			Toast.error(this.$t('settings.inquiry_form.error_fetching_form'));
			this.loading = false;
		}
	}

	/**
	 * Switch to default locale
	 *
	 * @return {void}
	 */
	private setLocaleToDefault(): void {
		if (this.restaurant.locales?.length > 1) {
			this.selectedLocale = this.restaurant.locales.find((locale: RestaurantLocale) => locale.is_default)!;
		}
	}

	/**
	 * Add localization fields for non-default selected locale if the fields don't exist
	 *
	 * @return {void}
	 */
	private addLocalizationFields(): void {
		const selectedLocale = this.selectedLocale.locale_short;
		if (this.mutableInquiryForm && !this.mutableInquiryForm.localization?.[selectedLocale] && !this.isDefaultLocale) {
			this.mutableInquiryForm.localization = {
				...(this.mutableInquiryForm?.localization || {}),
				[selectedLocale]: {
					title: '',
					description: ''
				}
			};
		}
	}

	/**
	 * Remove any localization keys that have no value set
	 *
	 * @return {void}
	 */
	private removeEmptyLocalization(): void {
		if (this.mutableInquiryForm?.localization) {
			Object.keys(this.mutableInquiryForm.localization).forEach((localeKey: string) => {
				const locale = this.mutableInquiryForm!.localization![localeKey];
				if (!locale) {
					delete this.mutableInquiryForm!.localization![localeKey];
					return;
				}
				Object.keys(locale).forEach((field: string) => {
					if (!locale[field]) {
						delete locale[field];
					}
				});
			});
		}
	}

	/**
	 * Expand the any section with a validation error then scroll to the first error
	 *
	 * @return {void}
	 */
	private scrollToFirstError(): void {
		const panels: ExpansionPanel[] = [this.$refs.formDetailsPanel, this.$refs.customFieldsPanel];

		for (const panel of panels) {
			if (panel.$el.querySelector('.error--text')) {
				panel.open = 0;
			}
		}

		setTimeout(() => {
			const firstError = document.querySelector('.inquiry-form-modal .error--text');
			if (firstError) {
				firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
			}
		}, 100);
	}

	/**
	 * Validate and save inquiry form
	 *
	 * @return {Promise<void>}
	 */
	private async saveModal(): Promise<void> {
		const isValid = await this.$refs.observer.validate();
		if (!isValid) {
			this.scrollToFirstError();
			return;
		}

		this.saving = true;
		try {
			if (this.mutableInquiryForm) {
				this.removeEmptyLocalization();
				await this.updatedInquiryForm(this.mutableInquiryForm);
				this.saving = false;
				Toast.success(this.$t('settings.inquiry_form.form_saved_successfully'));
				this.$emit('close');
			}
		}
		catch (errorMessage) {
			this.saving = false;
			Toast.error(this.$t('settings.inquiry_form.error_saving_form', { errorMessage }));
		}
	}
}
